import { useQuery, UseQueryResult } from '@tanstack/react-query';
import * as R from 'ramda';
import {
  DagLambdaFetch,
  UsersLambdaFetch,
  awaitJSON,
  fetchCometSelfInvoke
} from '../../utils/fetch-utils';
import {
  BuildStatusMap,
  BuildStatusRow,
  CompanyInfo,
  IngestStatusMap
} from './companies-constants';

// Cache key for the ingestion-availability query — exported so the ingest action can
// optimistically flip a button to disabled via queryClient.setQueryData.
export const INGEST_STATUSES_QUERY_KEY = ['companies', 'ingestStatuses'];

// The full company list (comet lambda), sorted by name and de-duplicated. Every company
// is included — there is intentionally no agency grouping/gating (that only existed for the
// now-removed agency filter and silently dropped agency-less companies).
export const useCompanyList = (email: string): UseQueryResult<CompanyInfo[]> =>
  useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['companies', 'list', email],
    retry: false,
    queryFn: async () => {
      // The lambda resolves the caller from the authorizer itself, so no email is sent; `email` only
      // keys the cache per user.
      const companies = await fetchCometSelfInvoke<Record<string, never>, CompanyInfo[]>(
        UsersLambdaFetch,
        '/getBasicCompanyInfo',
        {},
        { method: 'GET' }
      );
      return R.uniq(R.sortBy(company => company.name, companies));
    }
  });

// Build/batch status rows grouped by company_id. Internal-only.
export const useBuildStatusMap = (enabled: boolean): UseQueryResult<BuildStatusMap> =>
  useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['companies', 'buildStatus'],
    enabled,
    retry: false,
    queryFn: async () => {
      const rows = await awaitJSON<BuildStatusRow[]>(await UsersLambdaFetch('/buildStatus'));
      const grouped: BuildStatusMap = {};
      for (const row of rows) {
        if (!grouped[row.company_id]) {
          grouped[row.company_id] = [];
        }
        grouped[row.company_id].push(row);
      }
      return grouped;
    }
  });

// Which ingestions are currently available per company. Internal-only. Non-critical: if this
// fails, the query error is intentionally NOT surfaced via the error modal — the ingest buttons
// simply fall back to disabled, which is the safe default (see Companies.tsx).
export const useIngestStatuses = (enabled: boolean): UseQueryResult<IngestStatusMap> =>
  useQuery({
    queryKey: INGEST_STATUSES_QUERY_KEY,
    enabled,
    retry: false,
    queryFn: async () => {
      const res = await DagLambdaFetch('/check', { params: { getAll: true } });
      const { result } = await awaitJSON<{ result: IngestStatusMap }>(res);
      return result;
    }
  });
